import keys from 'lodash/keys';
import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';

import Icon from '../../../components/Icon/Icon';
import { ButtonT3Icons } from '../../../constants/Icon';
import { createBEM } from '../../../lib/utils/css';

import { ButtonT3Size, ButtonT3Theme, ButtonT3Type } from './constants';

import './ButtonT3.scss';

class ButtonT3 extends React.Component {
  /*
   * @property {node} children - the content of the button
   * @property {Function} onClick - the function that fires when the button is clicked
   * @property {Array} classes - optional class(es) to be appended to button element to modify style
   * @property {Boolean} isDisabled - if true, disables onClick and adds disabled styling
   * @property {Boolean} looksDisabled - if true, adds disabled styling, but click event will still fire
   * @property {String} textColor - if provided, applies a hexcode color value to the button text
   * @property {String} iconType - if provided, indicates which icon type should be displayed on the button
   */
  static propTypes = {
    children: PropTypes.node.isRequired,
    qaTag: PropTypes.string,
    textColor: PropTypes.string,
    onClick: PropTypes.func,
    isDisabled: PropTypes.bool,
    looksDisabled: PropTypes.bool,
    isHoverShaded: PropTypes.bool,
    isInline: PropTypes.bool,
    isBlock: PropTypes.bool,
    isRightAligned: PropTypes.bool,
    isFull: PropTypes.bool,
    isNoPadding: PropTypes.bool,
    isElongated: PropTypes.bool,
    isRounded: PropTypes.bool,
    isHidden: PropTypes.bool,
    isPillShaped: PropTypes.bool,
    isInverted: PropTypes.bool,
    iconType: PropTypes.oneOf(keys(ButtonT3Icons)),
    size: PropTypes.oneOf(values(ButtonT3Size)),
    theme: PropTypes.oneOf(values(ButtonT3Theme)),
    type: PropTypes.oneOf(values(ButtonT3Type)),
    ariaLabel: PropTypes.string,
    name: PropTypes.string,
    // Button Link props
    href: PropTypes.string,
    isTargetBlank: PropTypes.bool,
    isExternalLink: PropTypes.bool,
    query: PropTypes.object,
  };

  static defaultProps = {
    type: ButtonT3Type.BUTTON,
  };

  isButtonDisabled = () => {
    return (
      this.props.isDisabled ||
      this.props.looksDisabled ||
      (this.props.type === ButtonT3Type.BUTTON ? !(this.props.href || this.props.onClick) : false)
    );
  };

  renderIcon = () => {
    const { iconType } = this.props;
    if (iconType) {
      const iconClasses = createBEM('ButtonT3__icon', {
        [iconType]: true,
      });
      return <Icon className={iconClasses} glyph={ButtonT3Icons[iconType]} />;
    }
  };

  renderButton = classNames => {
    const { type, onClick, looksDisabled, isDisabled, qaTag, textColor, ariaLabel, name } =
      this.props;

    return (
      <button
        className={classNames}
        type={type}
        onClick={onClick}
        disabled={looksDisabled ? false : isDisabled}
        data-qa={qaTag}
        style={textColor ? { color: textColor } : null}
        aria-disabled={looksDisabled}
        aria-label={ariaLabel}
        name={name || qaTag}
      >
        {this.renderIcon()}
        {this.props.children}
      </button>
    );
  };

  renderAnchor = (classNames, target) => {
    const { onClick, href, qaTag } = this.props;

    return (
      <a className={classNames} target={target} onClick={onClick} href={href} data-qa={qaTag}>
        {this.props.children}
      </a>
    );
  };

  renderLink = classNames => {
    const { onClick, href, qaTag, query } = this.props;

    if (this.props.isTargetBlank) {
      return this.renderAnchor(classNames, '_blank');
    }
    if (this.props.isExternalLink) {
      return this.renderAnchor(classNames);
    }

    return (
      <Link className={classNames} to={href} onClick={onClick} data-qa={qaTag} query={query}>
        {this.props.children}
      </Link>
    );
  };

  render() {
    const { size, theme, href } = this.props;
    const classNames = createBEM('ButtonT3', {
      [size]: !!size,
      [theme]: !!theme,
      link: !!href,
      inline: this.props.isInline,
      block: this.props.isBlock,
      rightAligned: this.props.isRightAligned,
      full: this.props.isFull,
      hoverShade: this.props.isHoverShaded,
      disabled: this.isButtonDisabled(),
      noPadding: this.props.isNoPadding,
      pill: this.props.isPillShaped,
      rounded: this.props.isRounded,
      elongated: this.props.isElongated,
      'a11y--visuallyHidden': this.props.isHidden,
      inverted: this.props.isInverted,
    });
    return href ? this.renderLink(classNames) : this.renderButton(classNames);
  }
}

export default ButtonT3;
